/*
 * KM2 Rail — shared horizontal-rail engine for the homepage carousels.
 *
 * Plan 20260628-215142 (rail-lazy-scroll). ONE engine for the brand marquee, the
 * audience tile rails (Men single-row / Women multi-row), and the EBC carousel.
 * Extracted from the proven audience-tiles startAutoScroll() and merged with the
 * ebc-spotlight loadMore() lazy-paging shape, so the subtle drag/rAF logic lives
 * in exactly one place (per the user's "extract a shared module" decision).
 *
 * BEHAVIOUR (confirmed with the user):
 *   - Initial batch = batchSize items rendered (default 20).
 *   - MANUAL scroll that brings a row within ~half-a-batch of its loaded end
 *     fetches the NEXT batch and APPENDS it (loaded items are kept, never cleared,
 *     so the shopper progressively completes the list).
 *   - AUTO-scroll NEVER triggers a fetch — it only loops back over what is loaded.
 *     (The lazy check lives only in the manual handlers, never in the rAF frame.)
 *   - Manual scroll reaching the loaded end wraps to the first item (the rAF wrap
 *     at scrollWidth/2 handles this continuously, manual or auto).
 *   - Pause auto-scroll while a CARD is hovered; resume on leave. Manual input
 *     (wheel / touch / drag) pauses briefly then resumes.
 *   - prefers-reduced-motion: rows render as a static wrapped grid (CSS); no
 *     auto-scroll, no drag, no lazy (there is no horizontal scroll to trigger it).
 *
 * RENDER OWNERSHIP: this engine is data- and markup-agnostic. The caller supplies
 *   fetchBatch(limit) -> Promise<item[]>   (returns up to `limit` renderable items;
 *                                            [] when the source is exhausted)
 *   renderItem(item, globalIndex) -> Node  (the section builds its own XSS-safe DOM;
 *                                            clones for the seamless loop are made by
 *                                            cloneNode(true) + aria-hidden, so no
 *                                            untrusted data is ever re-injected.)
 *
 * Public API (window.KM2Rail):
 *   create({ host, rowCount, rowClass, trackClass, fetchBatch, renderItem,
 *            batchSize, trigger, speed, onFirstBatch, cardSelector }) -> { destroy() }
 *
 *   cardSelector (default 'a'): what counts as "the pointer is over a card" for the
 *   hover-pause. 'a' fits rails whose card root IS a link (marquee, audience tiles).
 *   Quick-add cards are <div class="ebc-card--qa"> roots (the button can't legally
 *   nest inside an <a>), so those callers pass '.ebc-card' — otherwise hovering the
 *   image or the quick-add button doesn't pause the auto-scroll (owner 2026-07-02).
 */
(function () {
  if (window.KM2Rail) return; // idempotent

  var prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)');

  function makeSeg() {
    var seg = document.createElement('div');
    seg.style.display = 'contents'; // invisible to layout: items flex/gap as if flat
    return seg;
  }

  function create(opts) {
    opts = opts || {};
    var host = opts.host;
    if (!host) return { destroy: function () {} };
    var rowCount = Math.max(1, Number(opts.rowCount) || 1);
    var batchSize = Math.max(1, Number(opts.batchSize) || 20);
    var trigger = Math.max(1, Number(opts.trigger) || 10);
    var speed = Number(opts.speed) || 60; // CONSTANT auto-scroll rate in pixels per second
    var fetchBatch = typeof opts.fetchBatch === 'function' ? opts.fetchBatch : null;
    var renderItem = typeof opts.renderItem === 'function' ? opts.renderItem : null;
    if (!fetchBatch || !renderItem) return { destroy: function () {} };
    // What counts as a hovered "card" for pause-on-hover (see header comment).
    var cardSel = opts.cardSelector || 'a';
    var cardHoverSel = cardSel.split(',').map(function (s) { return s.trim() + ':hover'; }).join(', ');

    // How close to the loaded end (in items) a MANUAL scroll must get before we
    // fetch the next batch: ~half a batch, shared across the rows. For a 20/10
    // single-row rail that's 10 items ("the 10th item of the batch"); for 2-row
    // Women it's 5 per row — same intent, balanced across rows.
    var bufferItems = Math.max(1, Math.round((batchSize - trigger) / rowCount));

    var rows = [];
    var globalIndex = 0;
    var loading = false;
    var exhausted = false;
    var viewedHigh = 0; // VIEWED counter numerator: monotonic high-water of cards scrolled through
    var rr = 0; // round-robin row pointer for batch distribution

    // ---- build rows -------------------------------------------------------
    for (var r = 0; r < rowCount; r++) {
      var rowEl = document.createElement('div');
      if (opts.rowClass) rowEl.className = opts.rowClass;
      var track = document.createElement('div');
      if (opts.trackClass) track.className = opts.trackClass;
      var primarySeg = makeSeg();
      var cloneSeg = makeSeg();
      track.appendChild(primarySeg);
      track.appendChild(cloneSeg);
      rowEl.appendChild(track);
      host.appendChild(rowEl);
      rows.push({ el: rowEl, track: track, primarySeg: primarySeg, cloneSeg: cloneSeg, primaryCount: 0 });
    }

    // ---- append a fetched batch (PRIMARY only — clones come later) --------
    // While the source still has more to give, the rail is a FINITE scroller: we append
    // primary items only and DON'T build the loop mirror yet. Looping early would wrap the
    // shopper back to the start before the next batch could load (the bug). The clone mirror
    // is built once — in enableLoop() — when the source is exhausted.
    function appendItems(items) {
      items.forEach(function (item) {
        var row = rows[rr % rowCount];
        rr++;
        var node = renderItem(item, globalIndex);
        globalIndex++;
        if (!node) return;
        row.primarySeg.appendChild(node);
        row.primaryCount++;
      });
      reportViewed(); // loaded grew → let the viewed cap (capped at loaded) grow with it
    }

    // Source exhausted → mirror the FULL loaded set into the clone segment so the seamless
    // -50% auto-scroll loop can engage (lazy-until-the-end, THEN loop). Idempotent per row.
    function enableLoop() {
      rows.forEach(function (row) {
        if (row.looped) return;
        row.looped = true;
        var kids = row.primarySeg.children;
        for (var i = 0; i < kids.length; i++) {
          var clone = kids[i].cloneNode(true); // clone, never re-render untrusted data
          clone.setAttribute('aria-hidden', 'true');
          clone.setAttribute('tabindex', '-1');
          row.cloneSeg.appendChild(clone);
        }
      });
    }

    // Report load progress to the caller (loaded card count + whether the source is exhausted)
    // so the section can render a "loaded / total" counter in its header (search-page style).
    function reportProgress() {
      if (typeof opts.onProgress !== 'function') return;
      var loaded = 0;
      for (var i = 0; i < rows.length; i++) loaded += rows[i].primaryCount;
      opts.onProgress(loaded, exhausted);
    }

    // VIEWED (counter numerator): high-water count of primary cards the shopper has scrolled
    // past/through, summed across rows, capped at the loaded count. Monotonic; emitted only when
    // the integer increases (throttle). looped rows carry a 2x clone mirror, so divide scrollWidth
    // by 2 to get the per-item width over the PRIMARY set only.
    function reportViewed() {
      if (typeof opts.onViewed !== 'function') return;
      var viewed = 0, loaded = 0;
      for (var i = 0; i < rows.length; i++) {
        var r = rows[i];
        loaded += r.primaryCount;
        if (!r.primaryCount) continue;
        var perItem = (r.track.scrollWidth / (r.looped ? 2 : 1)) / r.primaryCount;
        if (perItem > 0) {
          var rightEdge = r.el.scrollLeft + r.el.clientWidth;
          viewed += Math.min(r.primaryCount, Math.max(0, Math.round(rightEdge / perItem)));
        }
      }
      viewed = Math.min(viewed, loaded);
      if (viewed > viewedHigh) { viewedHigh = viewed; opts.onViewed(viewedHigh); }
    }

    function loadMore() {
      if (loading || exhausted || prefersReduced.matches) return;
      loading = true;
      Promise.resolve(fetchBatch(batchSize)).then(function (items) {
        loading = false;
        // Loop trigger #1: the backend returned NOTHING — nothing left to load.
        if (!items || !items.length) { exhausted = true; enableLoop(); reportProgress(); return; }
        appendItems(items);
        // Loop trigger #2: the backend returned FEWER than we asked for — the source ran dry.
        if (items.length < batchSize) { exhausted = true; enableLoop(); }
        reportProgress();
      }).catch(function (err) {
        // Loop trigger #3: the backend ERRORED — it can't return anything more. Treat as
        // exhausted and loop what's already loaded (better than a dead, half-loaded rail).
        //
        // Say so. This branch used to swallow the error completely, so a failed feed was
        // indistinguishable from an exhausted one: the rail simply stopped, and a reader had no
        // way to tell a working rail that ran out from a broken one that never started.
        console.error('[km2-rail] fetchBatch failed — treating the source as exhausted; the rail'
          + ' will loop what is already loaded. host=' + (host && host.className) + ' error=' + err);
        loading = false;
        exhausted = true;
        enableLoop();
        reportProgress();
      });
    }

    // ---- manual-only lazy trigger ----------------------------------------
    // Called ONLY from manual handlers (wheel / touch / drag) — never the rAF
    // frame — so auto-scroll cannot lazy-load.
    function maybeLoad(row) {
      if (loading || exhausted || !row.primaryCount) return;
      // LOADING phase = no clones yet, so track.scrollWidth is the primary content width and
      // the rail scrolls finitely. Pull the next batch as the shopper nears the loaded end.
      var maxScroll = row.track.scrollWidth - row.el.clientWidth;
      if (maxScroll <= 0) { loadMore(); return; } // doesn't overflow yet → keep filling
      var perItem = row.track.scrollWidth / row.primaryCount;
      if (row.el.scrollLeft >= maxScroll - bufferItems * perItem) loadMore();
    }

    // ---- per-row scroll engine (auto-scroll + drag + hover-pause + wrap) --
    function startRow(row) {
      if (prefersReduced.matches) return; // static wrapped grid; no engine
      var el = row.el, track = row.track;
      var cardHover = false;
      var manualUntil = 0;
      var dragging = false, moved = false, startX = 0, startScroll = 0, pointerDown = false;
      var last = 0;
      var onScreen = true;
      if ('IntersectionObserver' in window) {
        new IntersectionObserver(function (entries) {
          onScreen = entries[0].isIntersecting;
        }, { threshold: 0 }).observe(el);
      }
      function clock() { return performance.now(); }
      function half() { return track.scrollWidth / 2; }
      function bumpManual() { manualUntil = clock() + 1200; }
      row.__bumpManual = bumpManual; // exposed for the nudge() API (arrow buttons)

      // Pause ONLY when a card is under the cursor (gaps/section keep scrolling).
      // A card = opts.cardSelector (default 'a'); quick-add rails pass '.ebc-card'
      // because their card root is a <div> and the button/image must pause too.
      el.addEventListener('mouseover', function (e) {
        if (e.target.closest(cardSel)) cardHover = true;
      });
      el.addEventListener('mouseout', function (e) {
        if (!e.target.closest(cardSel)) return;
        // Leaving a card: stay paused when the pointer's DESTINATION (relatedTarget) is
        // still inside a card — moving image -> quick-add button crosses an element
        // boundary but never leaves the card. The :hover probe alone is timing-sensitive
        // across browsers (hover chain may not be recomputed when mouseout dispatches),
        // so check the destination first and use :hover only as the fallback.
        var dest = e.relatedTarget;
        if (dest && dest.closest && dest.closest(cardSel)) return;
        if (!el.querySelector(cardHoverSel)) cardHover = false;
      });

      // Manual scroll (trackpad / shift-wheel / touch) pauses then resumes + lazy.
      el.addEventListener('wheel', function () { bumpManual(); maybeLoad(row); }, { passive: true });
      el.addEventListener('touchmove', function () { bumpManual(); maybeLoad(row); }, { passive: true });
      el.addEventListener('touchstart', bumpManual, { passive: true });

      // Mouse drag-to-scroll — capture deferred until past threshold so a plain
      // click still reaches the card's link.
      el.addEventListener('pointerdown', function (e) {
        if (e.pointerType !== 'mouse') return;
        pointerDown = true; dragging = false; moved = false;
        startX = e.clientX; startScroll = el.scrollLeft;
      });
      el.addEventListener('pointermove', function (e) {
        if (!pointerDown) return;
        var dx = e.clientX - startX;
        if (!dragging && Math.abs(dx) > 8) {
          dragging = true; moved = true;
          el.classList.add('is-dragging');
          try { el.setPointerCapture(e.pointerId); } catch (err) {}
        }
        if (dragging) { el.scrollLeft = startScroll - dx; manualUntil = clock() + 1200; maybeLoad(row); }
      });
      function endDrag(e) {
        if (!pointerDown) return;
        pointerDown = false;
        if (dragging) {
          dragging = false; manualUntil = clock() + 1200;
          el.classList.remove('is-dragging');
          try { if (e && e.pointerId != null) el.releasePointerCapture(e.pointerId); } catch (err) {}
        }
      }
      el.addEventListener('pointerup', endDrag);
      el.addEventListener('pointercancel', endDrag);
      // Suppress the card's navigation click when the pointer was actually a drag.
      el.addEventListener('click', function (e) {
        if (moved) { e.preventDefault(); e.stopPropagation(); moved = false; }
      }, true);

      function frame(t) {
        if (!last) last = t;
        var dt = t - last; last = t;
        if (dt > 100) dt = 16; // a throttled/background frame must not jump-scroll
        var run = onScreen && !cardHover && !dragging && t >= manualUntil
          && !document.hidden && track.scrollWidth > el.clientWidth;
        // Auto-scroll at a CONSTANT pixels-per-second (opts.speed), independent of how much is
        // loaded — otherwise a longer rail (or one mid-lazy-load) accelerates as it grows.
        if (exhausted) {
          // LOOP phase: clones are in place; wrap seamlessly at the half-way point.
          var h = half();
          if (h > 0) {
            if (el.scrollLeft >= h) el.scrollLeft -= h;
            else if (el.scrollLeft < 0) el.scrollLeft += h;
            if (run) el.scrollLeft += speed * (dt / 1000);
          }
        } else {
          // LOADING phase: finite scroll, NO wrap. Auto-scroll marches toward the loaded end
          // and pulls the next batch as it nears it — continuous loading until the source is
          // exhausted (then the branch above takes over and loops).
          var maxScroll = track.scrollWidth - el.clientWidth;
          if (maxScroll > 0) {
            if (el.scrollLeft < 0) el.scrollLeft = 0;
            if (run) el.scrollLeft = Math.min(el.scrollLeft + speed * (dt / 1000), maxScroll);
            var perItem = track.scrollWidth / Math.max(1, row.primaryCount);
            if (el.scrollLeft >= maxScroll - bufferItems * perItem) loadMore();
          } else {
            loadMore(); // not overflowing yet → keep loading to fill the rail
          }
        }
        reportViewed(); // update the viewed high-water after this frame's scroll move
        requestAnimationFrame(frame);
      }
      requestAnimationFrame(frame);
    }

    // ---- boot -------------------------------------------------------------
    // `failed` distinguishes the two ways a first batch can come back empty. They are NOT the
    // same event and must not carry the same severity: a rail whose FETCH BROKE is a defect worth
    // an error, while a rail that fetched fine and simply has nothing to show (every candidate
    // out of stock — recently-viewed and the marquee filter to in-stock by design) is a normal,
    // expected state. Logging both as console.error made the ordinary case look like a fault:
    // it turned every homepage walk red in a dev session, where the REST availability endpoint
    // reports the whole catalogue unavailable, and it would have trained anyone reading the
    // console to ignore the very signal this marker exists to raise.
    var fetchFailed = false;
    Promise.resolve(fetchBatch(batchSize))
      .catch(function (err) {
        fetchFailed = true;
        try { console.error('[km2-rail] first batch FAILED to load. host=' + (host && host.className) + ' —', err); } catch (e) {}
        return [];
      })
      .then(function (items) {
      if (!items || !items.length) {
        // The rail never starts: startRow() is not reached, so nothing is wired to ask again.
        // What the SHOPPER sees depends on the host section, so don't assume one outcome:
        //   - a host with no removal path of its own is left as a heading over an empty row
        //     wrapper (.ebc-empty is never shown) — the case this marker was added for;
        //   - brand-marquee stays fully hidden, and the marker lands on an ATTACHED node;
        //   - recently-viewed / product-recommendations detach themselves first, so the
        //     marker below is set on an ORPHANED node and is NOT observable from the DOM.
        // That last case means this attribute is not a reliable cross-section probe; a spec
        // asserting it must target a host that stays mounted (verified by crawl, 2026-09-07).
        // Mark the host so a caller (or a spec) can tell "this rail booted empty" from "this rail
        // was never mounted", and say it once in the console. Cheap, and it makes a silent
        // failure diagnosable instead of invisible.
        try {
          if (host && host.setAttribute) host.setAttribute('data-km2-rail-empty', '1');
        } catch (e) {}
        // ALWAYS console.error here. An earlier attempt downgraded the "fetched fine, nothing to
        // show" case to console.info, on the theory that only a broken fetch deserves an error.
        // That theory does not survive contact with the code: fetchBatch is supplied by each
        // SECTION, and the EBC one catches its own abort and resolves to [] — so a genuinely dead
        // feed is indistinguishable here from an ordinary empty result, and the downgrade silenced
        // the dead-feed case that ebc-spotlight.spec.ts:280 exists to catch. If a caller ever needs
        // that distinction it has to come from fetchBatch itself, not be guessed at this layer.
        console.error('[km2-rail] first batch was empty — rail not started. host='
          + (host && host.className));
        return;
      }
      appendItems(items);
      if (items.length < batchSize) { exhausted = true; enableLoop(); } // whole list fit → loop now
      rows.forEach(startRow);
      reportProgress();
      if (typeof opts.onFirstBatch === 'function') opts.onFirstBatch(rows);
    }).catch(function (err) {
      try {
        if (host && host.setAttribute) host.setAttribute('data-km2-rail-error', '1');
      } catch (e) {}
      console.error('[km2-rail] first batch threw — rail not started. host='
        + (host && host.className) + ' error=' + err);
    });

    return {
      rows: rows,
      // Programmatic manual scroll (e.g. EBC arrow buttons): scroll a row, count it
      // as manual input (pauses auto-scroll briefly) and run the lazy check — so an
      // arrow tap loads the next batch just like a wheel/drag would.
      nudge: function (rowIndex, dx) {
        var row = rows[rowIndex || 0];
        if (!row) return;
        row.el.scrollLeft += dx;
        if (row.__bumpManual) row.__bumpManual();
        maybeLoad(row);
      },
      destroy: function () {
        rows.forEach(function (row) { if (row.el && row.el.parentNode) row.el.parentNode.removeChild(row.el); });
      }
    };
  }

  window.KM2Rail = { create: create };
})();
